<script setup lang="ts">
import { computed, ref } from 'vue'
import { MarButton, MarDialog, MarForm, MarSwitchInput, notify } from '@nimling/components-nimtech'
import type { PageView, View } from '@/types'
import { useViewsStore } from '@/stores/views'

const props = defineProps<{ pageView: PageView; view: View }>()

const emit = defineEmits<{ close: [result?: 'saved'] }>()

const viewsStore = useViewsStore()

const config = computed(() => (props.pageView.config ?? {}) as { annotations?: { hidden?: boolean } })

const showAnnotations = ref(!config.value.annotations?.hidden)
const saving = ref(false)


const putPlacement = async () => {
  saving.value = true
  try {
    const next = { ...config.value, annotations: { hidden: !showAnnotations.value } }
    await viewsStore.putPageView({ ...props.pageView, config: next })
    notify('success', 'Placement saved')
    emit('close', 'saved')
  } catch (err) {
    notify('error', err instanceof Error ? err.message : 'Placement save failed')
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <MarDialog :title="view.name" @close="emit('close')">
    <div class="placement-form">
      <p class="placement-hint">This placement of the view on this page: whether the annotations set on it are drawn. The view reads {{ view.signal }} from the store over the page range.</p>
      <MarForm>
        <MarSwitchInput v-model="showAnnotations" label="Show annotations" />
      </MarForm>
    </div>

    <template #footer>
      <MarButton label="Cancel" severity="secondary" tooltip="Close without saving" @click="emit('close')" />
      <MarButton label="Save" tooltip="Save this placement" :loading="saving" @click="putPlacement" />
    </template>
  </MarDialog>
</template>

<style scoped>
.placement-form {
  width: min(28em, 90vw);
  display: flex;
  flex-direction: column;
  gap: 1em;
}

.placement-hint {
  margin: 0;
  font-size: 0.8125em;
  color: var(--mar-text-muted-color);
}
</style>
